/**
 * Schedule Analysis — Overview tab.
 * Executive brief + KPI cards + completion drift, slip-by-window, DCMA trend charts.
 * Pure presentation: everything is derived from data the parent already holds
 * (stored events, latest windows run, latest DCMA run, score trend).
 */

import React, { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

export const ACCENT = '#0f766e';

export const CLASS_COLOR = {
  access: '#b45309',
  procurement: '#1d4ed8',
  engineering: '#7c3aed',
  weather: '#0369a1',
  change: '#b91c1c',
  other: '#475569',
};

export const CLASS_LABEL = {
  access: 'Access',
  procurement: 'Procurement',
  engineering: 'Engineering',
  weather: 'Weather',
  change: 'Change',
  other: 'Other',
};

const DAY_MS = 86400000;

function ts(iso) {
  if (!iso) return null;
  const t = Date.parse(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isFinite(t) ? t : null;
}

function fmtTs(t) {
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtTsFull(t) {
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function fmtIso(iso) {
  const t = ts(iso);
  return t == null ? (iso || '—') : fmtTsFull(t);
}

function fmtDateKey(key) {
  const k = String(key || '').replace(/-/g, '');
  if (!/^\d{8}$/.test(k)) return key || '—';
  return fmtIso(`${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}`);
}

/** "26LG0163-20260901" → "Sep 1"; falls back to the short name. */
export function revLabel(side) {
  const name = side?.projShortName || '';
  const m = name.match(/(\d{8})\s*$/);
  if (m) return fmtTs(ts(`${m[1].slice(0, 4)}-${m[1].slice(4, 6)}-${m[1].slice(6, 8)}`));
  if (/current/i.test(name)) return 'Current';
  if (side?.dataDate) return fmtTs(ts(side.dataDate));
  return name || '—';
}

/** Which parties have events on the driving path in this window. */
export function windowParty(w) {
  const groups = new Set(
    (w.eventsOnPath || []).map((e) => (!e.resp_party || /^lgl$/i.test(String(e.resp_party).trim()) ? 'lgl' : 'other'))
  );
  if (groups.has('lgl') && groups.has('other')) return 'concurrent';
  if (groups.has('other')) return 'other';
  if (groups.has('lgl')) return 'lgl';
  return 'none';
}

export const PARTY_STYLE = {
  concurrent: { color: '#b91c1c', label: 'Concurrent (LGL + others)' },
  other: { color: '#b45309', label: 'Third party on path' },
  lgl: { color: ACCENT, label: 'LGL on path' },
  none: { color: '#64748b', label: 'No events on path' },
};

const card = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: 14,
};

function Kpi({ label, value, sub, subColor = '#64748b', onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...card,
        textAlign: 'left',
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
      }}
    >
      <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
      <span style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>{value}</span>
      {sub && <span style={{ fontSize: 12, color: subColor }}>{sub}</span>}
    </button>
  );
}

export function ScoreRing({ score, size = 84, stroke = 9 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const color = score == null ? '#94a3b8' : score >= 80 ? '#15803d' : score >= 60 ? '#b45309' : '#b91c1c';
  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${c * pct} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" style={{ fontSize: size / 3.4, fontWeight: 800, fill: '#0f172a' }}>
        {score == null ? '—' : score}
      </text>
    </svg>
  );
}

const STAGES = [
  { id: 'events', label: 'Reading P6 notebooks' },
  { id: 'windows', label: 'Comparing weekly revisions' },
  { id: 'dcma', label: 'Scoring DCMA-14' },
];

function StageStrip({ runStage }) {
  const activeIdx = STAGES.findIndex((s) => s.id === runStage);
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      {STAGES.map((s, i) => {
        const done = activeIdx > i || runStage === 'done';
        const active = s.id === runStage;
        return (
          <span key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: done ? '#15803d' : active ? ACCENT : '#94a3b8', fontWeight: active ? 700 : 500 }}>
            <span style={{
              width: 8, height: 8, borderRadius: 99,
              background: done ? '#15803d' : active ? ACCENT : '#cbd5e1',
              animation: active ? 'pulse 1.2s ease-in-out infinite' : 'none',
            }} />
            {s.label}{done ? ' ✓' : active ? '…' : ''}
          </span>
        );
      })}
      <style>{'@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }'}</style>
    </div>
  );
}

const ScheduleAnalysisOverview = ({
  events = [],
  windows = null,
  dcma = null,
  trend = [],
  running = false,
  runStage = null,
  onRunFull,
  lastAnalyzedAt = null,
  goTab,
}) => {
  const wins = windows?.windows || [];

  const derived = useMemo(() => {
    // Completion drift: first window's "from", then every window's "to".
    const drift = [];
    if (wins.length && wins[0].from?.projectFinish && wins[0].from?.dataDate) {
      drift.push({ x: ts(wins[0].from.dataDate), y: ts(wins[0].from.projectFinish), label: revLabel(wins[0].from) });
    }
    for (const w of wins) {
      if (w.to?.projectFinish && w.to?.dataDate) {
        drift.push({ x: ts(w.to.dataDate), y: ts(w.to.projectFinish), label: revLabel(w.to) });
      }
    }
    const driftOk = drift.filter((p) => p.x != null && p.y != null);
    const totalDriftDays = driftOk.length >= 2
      ? Math.round((driftOk[driftOk.length - 1].y - driftOk[0].y) / DAY_MS)
      : null;

    const slipBars = wins.map((w) => {
      const party = windowParty(w);
      return {
        name: `${revLabel(w.from)}→${revLabel(w.to)}`,
        slip: w.projectFinishSlipDays ?? w.pathSlipDays ?? 0,
        pathSlip: w.pathSlipDays ?? 0,
        party,
        color: PARTY_STYLE[party].color,
        events: (w.eventsOnPath || []).length,
      };
    });

    let biggest = null;
    for (const b of slipBars) {
      if (!biggest || Math.abs(b.slip) > Math.abs(biggest.slip)) biggest = b;
    }

    // Top drivers: cumulative positive finish slip on the driving path.
    const agg = new Map();
    for (const w of wins) {
      const rows = (w.pathSlips?.length ? w.pathSlips : (w.topSlips || []).filter((s) => s.onDrivingPath && (s.finishSlipDays || 0) > 0));
      for (const s of rows) {
        const cur = agg.get(s.taskCode) || { taskCode: s.taskCode, taskName: s.taskName, days: 0, party: null };
        cur.days += Math.max(0, s.finishSlipDays || 0);
        if (s.respParty) cur.party = s.respParty;
        else if (!cur.party && s.partyGroup === 'lgl') cur.party = 'LGL';
        agg.set(s.taskCode, cur);
      }
    }
    const drivers = [...agg.values()].filter((d) => d.days > 0).sort((a, b) => b.days - a.days).slice(0, 8);
    const maxDriver = drivers[0]?.days || 1;

    const byClass = {};
    const bySource = {};
    let latestKey = null;
    for (const e of events) {
      const c = e.classification || 'other';
      byClass[c] = (byClass[c] || 0) + 1;
      bySource[e.source] = (bySource[e.source] || 0) + 1;
      const k = String(e.date_key || '').replace(/-/g, '');
      if (/^\d{8}$/.test(k) && (!latestKey || k > latestKey)) latestKey = k;
    }
    const classRows = Object.entries(byClass).sort((a, b) => b[1] - a[1]);
    const maxClass = classRows[0]?.[1] || 1;

    const concurrentCount = wins.filter((w) => w.concurrentDelay).length;

    const trendData = (trend || [])
      .map((p) => ({ x: ts(p.data_date) || Date.parse(p.created_at), score: Number(p.score) }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.score));
    const prevScore = trendData.length >= 2 ? trendData[trendData.length - 2].score : null;

    return {
      drift: driftOk, totalDriftDays, slipBars, biggest, drivers, maxDriver,
      classRows, maxClass, bySource, latestKey, concurrentCount, trendData, prevScore,
    };
  }, [wins, events, trend]);

  const {
    drift, totalDriftDays, slipBars, biggest, drivers, maxDriver,
    classRows, maxClass, bySource, latestKey, concurrentCount, trendData, prevScore,
  } = derived;

  const forecastFinish = wins.length ? wins[wins.length - 1]?.to?.projectFinish : null;
  const scoreDelta = dcma?.score != null && prevScore != null ? dcma.score - prevScore : null;

  // Executive brief — deterministic sentences from the numbers, nothing invented.
  const brief = useMemo(() => {
    const lines = [];
    if (drift.length >= 2 && totalDriftDays != null) {
      const dir = totalDriftDays > 0 ? 'slipped' : totalDriftDays < 0 ? 'recovered' : 'held';
      lines.push(`The forecast completion ${dir}${totalDriftDays ? ` ${Math.abs(totalDriftDays)} calendar days` : ''} across ${wins.length} compared revision${wins.length === 1 ? '' : 's'} — from ${fmtTsFull(drift[0].y)} to ${fmtTsFull(drift[drift.length - 1].y)}.`);
    }
    if (biggest && Math.abs(biggest.slip) > 0) {
      lines.push(`The biggest single move was ${Math.abs(biggest.slip)} days in the ${biggest.name} window (${PARTY_STYLE[biggest.party].label.toLowerCase()}, ${biggest.events} event${biggest.events === 1 ? '' : 's'} on the driving path).`);
    }
    if (drivers.length) {
      const d = drivers[0];
      lines.push(`Largest driver on the driving path: ${d.taskCode} ${d.taskName || ''} (+${Math.round(d.days)} days${d.party ? `, ${d.party}` : ''}).`);
    }
    if (wins.length) {
      lines.push(concurrentCount > 0
        ? `${concurrentCount} of ${wins.length} windows show concurrent delay — LGL and third parties both had events on the driving path there.`
        : 'No concurrent-delay windows: driving-path events in each window belong to a single party.');
    }
    if (dcma?.score != null) {
      const fails = (dcma.metrics || []).filter((m) => !m.pass && m.value != null).slice(0, 3).map((m) => m.name.split('(')[0].trim());
      lines.push(`Schedule health is ${dcma.score}/100 (${dcma.passCount} of ${dcma.metricCount} checks pass${scoreDelta != null && scoreDelta !== 0 ? `, ${scoreDelta > 0 ? 'up' : 'down'} ${Math.abs(scoreDelta)} since the previous run` : ''}).${fails.length ? ` Failing: ${fails.join(', ')}.` : ''}`);
    }
    if (events.length) {
      const src = Object.entries(bySource).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(', ');
      lines.push(`${events.length} delay events on file (${src})${latestKey ? `; most recent ${fmtDateKey(latestKey)}` : ''}.`);
    }
    return lines;
  }, [drift, totalDriftDays, wins.length, biggest, drivers, concurrentCount, dcma, scoreDelta, events.length, bySource, latestKey]);

  const empty = !events.length && !wins.length && !dcma;

  if (empty) {
    return (
      <div style={{ ...card, padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🧭</div>
        <h3 style={{ margin: '0 0 6px' }}>No analysis yet for this job</h3>
        <p style={{ color: '#64748b', fontSize: 13, maxWidth: 520, margin: '0 auto 16px' }}>
          One click reads the P6 notebooks into a delay register, compares every weekly revision
          for slip and driving-path changes, and scores DCMA-14.
        </p>
        {running ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}><StageStrip runStage={runStage} /></div>
        ) : (
          <button
            type="button"
            onClick={onRunFull}
            style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: ACCENT, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
          >
            Run full analysis
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Run bar */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 14px' }}>
        {running
          ? <StageStrip runStage={runStage} />
          : (
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {lastAnalyzedAt ? `Last analyzed ${new Date(lastAnalyzedAt).toLocaleString()}` : 'Stored results shown — re-run for the latest P6 state.'}
            </span>
          )}
        <button
          type="button"
          onClick={onRunFull}
          disabled={running}
          style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: running ? '#94a3b8' : ACCENT, color: '#fff', fontWeight: 700, fontSize: 13, cursor: running ? 'default' : 'pointer' }}
        >
          {running ? 'Analyzing…' : 'Run full analysis'}
        </button>
      </div>

      {/* Executive brief */}
      {brief.length > 0 && (
        <div style={{ ...card, borderLeft: `4px solid ${ACCENT}` }}>
          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Executive brief</div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {brief.map((line, i) => (
              <li key={i} style={{ fontSize: 13.5, color: '#1e293b', lineHeight: 1.5 }}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
        <Kpi
          label="Forecast finish"
          value={forecastFinish ? fmtIso(forecastFinish) : '—'}
          sub={totalDriftDays != null ? `${totalDriftDays > 0 ? '+' : ''}${totalDriftDays} d since first compared rev` : 'run windows for drift'}
          subColor={totalDriftDays > 0 ? '#b91c1c' : totalDriftDays < 0 ? '#15803d' : '#64748b'}
          onClick={() => goTab?.('windows')}
        />
        <Kpi
          label="DCMA-14 health"
          value={dcma?.score != null ? `${dcma.score} / 100` : '—'}
          sub={dcma ? `${dcma.passCount} of ${dcma.metricCount} pass${scoreDelta != null && scoreDelta !== 0 ? ` · ${scoreDelta > 0 ? '▲' : '▼'} ${Math.abs(scoreDelta)}` : ''}` : 'not scored yet'}
          subColor={scoreDelta > 0 ? '#15803d' : scoreDelta < 0 ? '#b91c1c' : '#64748b'}
          onClick={() => goTab?.('health')}
        />
        <Kpi
          label="Latest window"
          value={wins.length ? `${(wins[wins.length - 1].projectFinishSlipDays ?? wins[wins.length - 1].pathSlipDays) > 0 ? '+' : ''}${wins[wins.length - 1].projectFinishSlipDays ?? wins[wins.length - 1].pathSlipDays} d` : '—'}
          sub={wins.length ? `${revLabel(wins[wins.length - 1].from)} → ${revLabel(wins[wins.length - 1].to)}${wins[wins.length - 1].concurrentDelay ? ' · concurrent' : ''}` : 'no windows yet'}
          subColor={wins.length && wins[wins.length - 1].concurrentDelay ? '#b91c1c' : '#64748b'}
          onClick={() => goTab?.('windows')}
        />
        <Kpi
          label="Delay events"
          value={events.length}
          sub={latestKey ? `latest ${fmtDateKey(latestKey)}` : 'refresh from notebooks'}
          onClick={() => goTab?.('events')}
        />
        <Kpi
          label="Windows compared"
          value={wins.length}
          sub={concurrentCount ? `${concurrentCount} concurrent` : 'none concurrent'}
          subColor={concurrentCount ? '#b91c1c' : '#64748b'}
          onClick={() => goTab?.('windows')}
        />
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 12 }}>
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Completion drift</div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>Forecast project finish at each weekly P6 revision</div>
          {drift.length >= 2 ? (
            <ResponsiveContainer width="100%" height={210}>
              <LineChart data={drift} margin={{ top: 6, right: 12, left: 4, bottom: 2 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="x" type="number" domain={['dataMin', 'dataMax']} tickFormatter={fmtTs} tick={{ fontSize: 11 }} />
                <YAxis dataKey="y" type="number" domain={[`dataMin - ${3 * DAY_MS}`, `dataMax + ${3 * DAY_MS}`]} tickFormatter={fmtTs} tick={{ fontSize: 11 }} width={64} />
                <Tooltip
                  labelFormatter={(v) => `Data date ${fmtTsFull(v)}`}
                  formatter={(v) => [fmtTsFull(v), 'Forecast finish']}
                />
                <Line type="stepAfter" dataKey="y" stroke={ACCENT} strokeWidth={2.5} dot={{ r: 3.5, fill: ACCENT }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: 13, padding: '30px 0', textAlign: 'center' }}>
              Re-run the analysis to compute finish drift (needs at least two dated revisions).
            </div>
          )}
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Slip by window</div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
            Days of finish movement per week ·
            <span style={{ color: ACCENT }}> ■ LGL</span>
            <span style={{ color: '#b45309' }}> ■ third party</span>
            <span style={{ color: '#b91c1c' }}> ■ concurrent</span>
            <span style={{ color: '#64748b' }}> ■ no events on path</span>
          </div>
          {slipBars.length ? (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={slipBars} margin={{ top: 6, right: 8, left: 4, bottom: 2 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={48} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                <Tooltip formatter={(v, n, p) => [`${v} d (path max ${p?.payload?.pathSlip} d, ${p?.payload?.events} events on path)`, 'Slip']} />
                <Bar dataKey="slip" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {slipBars.map((b, i) => <Cell key={i} fill={b.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: 13, padding: '30px 0', textAlign: 'center' }}>No windows yet.</div>
          )}
        </div>
      </div>

      {/* Drivers + breakdowns */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Top delay drivers (cumulative days on the driving path)</div>
          {drivers.length ? drivers.map((d) => (
            <div key={d.taskCode} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                <span style={{ color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>
                  <b>{d.taskCode}</b> {d.taskName || ''}
                </span>
                <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                  +{Math.round(d.days)} d{d.party ? ` · ${d.party}` : ''}
                </span>
              </div>
              <div style={{ height: 7, background: '#f1f5f9', borderRadius: 99 }}>
                <div style={{ height: '100%', width: `${Math.min(100, (d.days / maxDriver) * 100)}%`, background: d.party && !/^lgl$/i.test(d.party) ? '#b45309' : ACCENT, borderRadius: 99 }} />
              </div>
            </div>
          )) : (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>No driving-path slip recorded across windows.</div>
          )}
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Delay events by cause</div>
          {classRows.length ? classRows.map(([cls, n]) => (
            <div key={cls} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                <span style={{ color: CLASS_COLOR[cls] || '#475569', fontWeight: 600 }}>{CLASS_LABEL[cls] || cls}</span>
                <span style={{ color: '#64748b' }}>{n}</span>
              </div>
              <div style={{ height: 7, background: '#f1f5f9', borderRadius: 99 }}>
                <div style={{ height: '100%', width: `${(n / maxClass) * 100}%`, background: CLASS_COLOR[cls] || '#475569', borderRadius: 99 }} />
              </div>
            </div>
          )) : (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>No events stored yet.</div>
          )}
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>DCMA-14 health</div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <ScoreRing score={dcma?.score ?? null} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {trendData.length >= 2 ? (
                <ResponsiveContainer width="100%" height={90}>
                  <LineChart data={trendData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <XAxis dataKey="x" type="number" domain={['dataMin', 'dataMax']} tickFormatter={fmtTs} tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={26} />
                    <Tooltip labelFormatter={(v) => fmtTsFull(v)} formatter={(v) => [v, 'Score']} />
                    <Line type="monotone" dataKey="score" stroke={ACCENT} strokeWidth={2} dot={{ r: 2.5 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Score trend appears after two runs.</div>
              )}
              {dcma && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  {(dcma.metrics || []).filter((m) => !m.pass && m.value != null).length} failing checks — details on the Health tab.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleAnalysisOverview;
